import { BindingError } from "../../BindingError.js";
import { MarkerComparator } from "../MarkerComparator.js";
import { OperationsHelpers } from "./OperationsHelpers.js";
import { ErrorLocator } from "../exceptions/ErrorLocator.js";
import { ServerId, UnpersistedEntityDummyId } from "../../accessorTree/RuntimeId.js";
import { EventListenersStore } from "../../treeParameters/EventListenersStore.js";
import { getEntityMarker } from "../state/getEntityMarker.js";
import { StateIterator } from "../state/StateIterator.js";
import { LocalizedBindingError } from "../exceptions/LocalizedBindingError.js";
import { assertNever } from "../../utils/assertNever.js";
import { HasOneRelationMarker } from "../../markers/HasOneRelationMarker.js";
class EntityOperations {
  constructor(accessorErrorManager, batchUpdatesOptions, eventManager, stateInitializer, treeStore) {
    this.accessorErrorManager = accessorErrorManager;
    this.batchUpdatesOptions = batchUpdatesOptions;
    this.eventManager = eventManager;
    this.stateInitializer = stateInitializer;
    this.treeStore = treeStore;
  }
  addError(entityRealm, error) {
    return this.accessorErrorManager.addError(entityRealm, error);
  }
  addEventListener(state, event, listener) {
    if (state.eventListeners === void 0) {
      state.eventListeners = new EventListenersStore();
    }
    return state.eventListeners.add(event, listener);
  }
  batchUpdates(state, performUpdates) {
    this.eventManager.syncOperation(() => {
      performUpdates(state.getAccessor, this.batchUpdatesOptions);
    });
  }
  connectEntityAtField(outerState, fieldName, entityToConnect) {
    this.eventManager.syncOperation(() => {
      const stateToConnect = OperationsHelpers.resolveAndPrepareEntityToConnect(this.treeStore, entityToConnect);
      const fieldsToConnect = getEntityMarker(stateToConnect).fields;
      const persistedData = this.treeStore.persistedEntityData.get(outerState.entity.id.uniqueValue);
      const entityName = outerState.entity.entityName;
      const fieldSchema = this.treeStore.schema.getEntityFieldOrUndefined(entityName, fieldName);
      if (fieldSchema === void 0) {
        throw new BindingError(
          `EntityAccessor.connectEntityAtField: Unknown field ${entityName}.${fieldName}.

Entity located at: ${ErrorLocator.locateInternalState(outerState)}.`
        );
      } else if (fieldSchema.__typename === "_Column") {
        throw new BindingError(
          `EntityAccessor.connectEntityAtField: Cannot connect at field ${entityName}.${fieldName} because it's not a has-one relation.

Entity located at: ${ErrorLocator.locateInternalState(outerState)}.`
        );
      }
      for (const state of StateIterator.eachSiblingRealm(outerState)) {
        const targetHasOneMarkers = this.resolveHasOneRelationMarkers(
          getEntityMarker(state).fields,
          fieldName,
          `Cannot connect at field '${fieldName}' as it doesn't refer to a has one relation.`
        );
        for (const targetHasOneMarker of targetHasOneMarkers) {
          const previouslyConnectedState = state.children.get(targetHasOneMarker.placeholderName);
          if (previouslyConnectedState === void 0 || previouslyConnectedState.type === "field" || previouslyConnectedState.type === "entityList") {
            OperationsHelpers.rejectInvalidAccessorTree();
          }
          if (previouslyConnectedState.entity.entityName !== stateToConnect.entity.entityName) {
            throw new BindingError(
              `EntityAccessor.connectEntityAtField: Attempting to connect at field ${entityName}.${fieldName} an entity of type '${stateToConnect.entity.entityName}' but '${previouslyConnectedState.entity.entityName}' is expected.

Entity located at: ${ErrorLocator.locateInternalState(outerState)}.`
            );
          }
          try {
            MarkerComparator.assertEntityMarkersSubsetOf(targetHasOneMarker.fields, fieldsToConnect);
          } catch (error) {
            if (error instanceof LocalizedBindingError) {
              throw new BindingError(
                `EntityAccessor: cannot connect entity with key '${entityToConnect.key}' because its fields areincompatible with entities found at field '${fieldName}'. Make sure both trees are equivalent.

${error.message}

` + (error.markerPath.length > 1 ? `Incompatibility found at: ${ErrorLocator.locateMarkerPath(error.markerPath)}.

` : "") + `Entity located at: ${ErrorLocator.locateInternalState(state)}.`
              );
            }
            throw error;
          }
          if (previouslyConnectedState === stateToConnect || previouslyConnectedState.entity.id.value === stateToConnect.entity.id.value) {
            continue;
          }
          this.treeStore.disposeOfRealm(previouslyConnectedState);
          let changesDelta = 0;
          if (previouslyConnectedState.type === "entityRealm") {
            changesDelta -= previouslyConnectedState.unpersistedChangesCount;
          }
          const persistedId = persistedData?.get(targetHasOneMarker.placeholderName);
          if (persistedId instanceof ServerId && persistedId.value === stateToConnect.entity.id.value) {
            changesDelta--;
          } else if (persistedId instanceof ServerId && persistedId.value === previouslyConnectedState.entity.id.value) {
            changesDelta++;
          } else if (!previouslyConnectedState.entity.id.existsOnServer) {
            if (persistedId === null || // We're updating.
            persistedId === void 0 && // We're creating.
            (!fieldsToConnect.hasAtLeastOneBearingField || !targetHasOneMarker.parameters.isNonbearing)) {
              changesDelta++;
            }
          }
          if (stateToConnect.type === "entityRealm") {
            changesDelta += stateToConnect.unpersistedChangesCount;
          }
          this.stateInitializer.initializeEntityRealm(
            stateToConnect.entity.id,
            stateToConnect.entity.entityName,
            previouslyConnectedState.blueprint,
            stateToConnect
          );
          this.eventManager.registerUpdatedConnection(state, targetHasOneMarker.placeholderName);
          this.eventManager.registerJustUpdated(state, changesDelta);
        }
      }
    });
  }
  disconnectEntityAtField(outerState, fieldName, initializeReplacement) {
    this.eventManager.syncOperation(() => {
      const persistedData = this.treeStore.persistedEntityData.get(outerState.entity.id.uniqueValue);
      for (const state of StateIterator.eachSiblingRealm(outerState)) {
        const targetHasOneMarkers = this.resolveHasOneRelationMarkers(
          getEntityMarker(state).fields,
          fieldName,
          `Cannot disconnect at field '${fieldName}' as it doesn't refer to a has one relation.`
        );
        for (const targetHasOneMarker of targetHasOneMarkers) {
          const stateToDisconnect = state.children.get(targetHasOneMarker.placeholderName);
          if (stateToDisconnect === void 0 || stateToDisconnect.type !== "entityRealm" && stateToDisconnect.type !== "entityRealmStub") {
            OperationsHelpers.rejectInvalidAccessorTree();
          }
          this.treeStore.disposeOfRealm(stateToDisconnect);
          let changesDelta = 0;
          if (stateToDisconnect.type === "entityRealm") {
            changesDelta -= stateToDisconnect.unpersistedChangesCount;
          }
          const persistedId = persistedData?.get(targetHasOneMarker.placeholderName);
          if (persistedId instanceof ServerId) {
            if (persistedId.value === stateToDisconnect.entity.id.value) {
              changesDelta++;
            }
          } else if (persistedId === void 0 || persistedId === null) {
            if (stateToDisconnect.entity.id.existsOnServer) {
              changesDelta--;
            }
          } else
            ;
          const newEntity = this.stateInitializer.initializeEntityRealm(
            new UnpersistedEntityDummyId(),
            stateToDisconnect.entity.entityName,
            stateToDisconnect.blueprint
          );
          this.stateInitializer.runImmediateUserInitialization(newEntity, initializeReplacement);
          this.eventManager.registerUpdatedConnection(state, targetHasOneMarker.placeholderName);
          this.eventManager.registerJustUpdated(state, changesDelta);
        }
      }
    });
  }
  deleteEntity(realm) {
    this.eventManager.syncOperation(() => {
      const outerState = realm.entity;
      outerState.isScheduledForDeletion = true;
      const entityId = outerState.id;
      for (const realmToDelete of outerState.realms.values()) {
        let parent;
        let changesDelta = 0;
        if (realmToDelete.blueprint.type === "listEntity") {
          parent = realmToDelete.blueprint.parent;
          parent.children.delete(entityId.value);
          if (entityId.existsOnServer) {
            if (parent.plannedRemovals === void 0) {
              parent.plannedRemovals = /* @__PURE__ */ new Map();
            }
            changesDelta++;
            parent.plannedRemovals.set(entityId.value, "delete");
          }
        } else if (realmToDelete.blueprint.type === "hasOne") {
          parent = realmToDelete.blueprint.parent;
          const placeholderName = realmToDelete.blueprint.marker.placeholderName;
          if (entityId.existsOnServer) {
            if (parent.plannedHasOneDeletions === void 0) {
              parent.plannedHasOneDeletions = /* @__PURE__ */ new Map();
            }
            changesDelta++;
            parent.plannedHasOneDeletions.set(placeholderName, realmToDelete);
          }
          this.stateInitializer.initializeEntityRealm(
            new UnpersistedEntityDummyId(),
            outerState.entityName,
            realmToDelete.blueprint
          );
          this.eventManager.registerUpdatedConnection(parent, placeholderName);
        } else if (realmToDelete.blueprint.type === "subTree") {
          parent = void 0;
          if (realmToDelete.type === "entityRealm") {
            this.eventManager.registerJustUpdated(realmToDelete, 1);
          }
        } else {
          return assertNever(realmToDelete.blueprint);
        }
        if (parent) {
          if (realmToDelete.type === "entityRealm") {
            changesDelta -= realmToDelete.unpersistedChangesCount;
          }
          this.eventManager.registerJustUpdated(parent, changesDelta);
          if (realmToDelete.type === "entityRealm") {
            parent.childrenWithPendingUpdates?.delete(realmToDelete);
          }
          this.treeStore.disposeOfRealm(realmToDelete);
        }
      }
    });
  }
  *resolveHasOneRelationMarkers(container, field, message) {
    const placeholders = container.placeholders.get(field);
    if (placeholders === void 0) {
      return;
    }
    const normalizedPlaceholders = placeholders instanceof Set ? placeholders : [placeholders];
    for (const placeholderName of normalizedPlaceholders) {
      const hasOneRelation = container.markers.get(placeholderName);
      if (!(hasOneRelation instanceof HasOneRelationMarker)) {
        throw new BindingError(message);
      }
      yield hasOneRelation;
    }
  }
}
export {
  EntityOperations
};
//# sourceMappingURL=EntityOperations.js.map
